export type KontoArt = 'AKTIVA' | 'PASSIVA'
export type Buchungstyp = 'EINGANG' | 'AUSGANG'
export type Periode = 'MONATLICH' | 'ZWEIMONATLICH' | 'QUARTALSWEISE' | 'JAEHRLICH'

export interface Konto {
  id: number
  art: KontoArt
  bezeichnung: string
  eingang: number
  ausgang: number
  saldo: number
}

export interface Buchung {
  id: number
  buchungsnummer: number
  name: string
  beschreibung: string
  wert: number
  datum: string
  typ: Buchungstyp
  kontoId: number | null
  kontoBezeichnung: string | null
  gegenkontoId: number | null
  gegenkontoBezeichnung: string | null
  dauerauftragId: number | null
}

export interface BuchungEingabe {
  name: string
  beschreibung: string
  wert: string
  datum: string
  typ: Buchungstyp
  kontoId: number | null
  gegenkontoId: number | null
}

export interface Dauerauftrag {
  id: number
  periode: Periode
  name: string
  beschreibung: string
  wert: number
  buchungstyp: Buchungstyp
  start: string
  ende: string | null
  kontoId: number | null
  kontoBezeichnung: string | null
  gegenkontoId: number | null
  gegenkontoBezeichnung: string | null
  letzteBuchung: string | null
}

export interface DauerauftragEingabe {
  periode: Periode
  name: string
  beschreibung: string
  wert: string
  buchungstyp: Buchungstyp
  start: string
  ende: string | null
  kontoId: number | null
  gegenkontoId: number | null
  ausBuchungId?: number | null
}

export interface ProtokollEintrag {
  id: number
  zeitpunkt: string
  rubrik: string
  eintrag: string
}

export type Rolle = 'ADMIN' | 'USER'

export interface Benutzer {
  id: number
  benutzername: string
  email: string | null
  emailVerifiziert: boolean
  rolle: Rolle
  aktiv: boolean
}

export interface Profil {
  benutzername: string
  email: string | null
  emailVerifiziert: boolean
  ausstehendeEmail: string | null
}

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    meldung: string,
  ) {
    super(meldung)
  }
}

function csrfToken(): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith('XSRF-TOKEN='))
    ?.substring('XSRF-TOKEN='.length)
}

async function anfrage<T>(methode: string, pfad: string, body?: unknown, form?: URLSearchParams): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const token = csrfToken()
  if (token && methode !== 'GET') headers['X-XSRF-TOKEN'] = decodeURIComponent(token)
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const antwort = await fetch(pfad, {
    method: methode,
    headers,
    credentials: 'same-origin',
    body: form ?? (body !== undefined ? JSON.stringify(body) : undefined),
  })
  if (!antwort.ok) {
    let meldung = `${antwort.status} ${antwort.statusText}`
    try {
      const fehler = await antwort.json()
      if (fehler?.fehler) meldung = fehler.fehler
    } catch {
      // keine JSON-Fehlermeldung
    }
    throw new ApiFehler(antwort.status, meldung)
  }
  if (antwort.status === 204) return undefined as T
  return antwort.json() as Promise<T>
}

const get = <T,>(pfad: string) => anfrage<T>('GET', pfad)
const post = <T,>(pfad: string, body?: unknown) => anfrage<T>('POST', pfad, body)
const put = <T,>(pfad: string, body: unknown) => anfrage<T>('PUT', pfad, body)
const del = (pfad: string) => anfrage<void>('DELETE', pfad)

export const api = {
  health: () => get<{ status: string }>('/api/health'),
  ich: () => get<{ benutzername: string; rollen: string[] }>('/api/auth/ich'),
  anmelden: async (benutzername: string, passwort: string) => {
    await api.health() // stellt sicher, dass das CSRF-Cookie gesetzt ist
    return anfrage<void>('POST', '/api/auth/login', undefined, new URLSearchParams({ username: benutzername, password: passwort }))
  },
  abmelden: () => post<void>('/api/auth/logout'),
  /** Oeffentliche Aufrufe (ohne Login) brauchen vorher das CSRF-Cookie, siehe anmelden. */
  oeffentlich: async <T,>(pfad: string, body: unknown) => {
    await api.health()
    return post<T>(pfad, body)
  },
  einladungAnnehmen: (token: string, passwort: string) => api.oeffentlich<void>('/api/auth/einladung', { token, passwort }),
  emailBestaetigen: (token: string) => api.oeffentlich<void>('/api/auth/email-bestaetigen', { token }),
  passwortVergessen: (benutzername: string) => api.oeffentlich<void>('/api/auth/passwort-vergessen', { benutzername }),
  passwortZuruecksetzen: (token: string, passwort: string) =>
    api.oeffentlich<void>('/api/auth/passwort-zuruecksetzen', { token, passwort }),

  profil: () => get<Profil>('/api/profil'),
  passwortAendern: (aktuell: string, neu: string) => put<void>('/api/profil/passwort', { aktuell, neu }),
  emailAendern: (email: string) => put<void>('/api/profil/email', { email }),

  benutzer: () => get<Benutzer[]>('/api/benutzer'),
  benutzerAnlegen: (e: { benutzername: string; email: string; rolle: Rolle }) => post<Benutzer>('/api/benutzer', e),
  einladungSenden: (id: number) => post<void>(`/api/benutzer/${id}/einladung`),
  benutzerLoeschen: (id: number) => del(`/api/benutzer/${id}`),

  konten: () => get<Konto[]>('/api/konten'),
  kontoAnlegen: (e: { art: KontoArt; bezeichnung: string }) => post<Konto>('/api/konten', e),
  kontoAendern: (id: number, e: { art: KontoArt; bezeichnung: string }) => put<Konto>(`/api/konten/${id}`, e),
  kontoLoeschen: (id: number) => del(`/api/konten/${id}`),
  kontoBuchungen: (id: number) => get<{ ausgaenge: Buchung[]; eingaenge: Buchung[] }>(`/api/konten/${id}/buchungen`),

  buchungen: (suche: string, seite: number, groesse = 50) =>
    get<{ eintraege: Buchung[]; gesamt: number }>(
      `/api/buchungen?${new URLSearchParams({ suche, seite: String(seite), groesse: String(groesse) })}`,
    ),
  buchungAnlegen: (e: BuchungEingabe) => post<Buchung>('/api/buchungen', e),
  buchungAendern: (id: number, e: BuchungEingabe) => put<Buchung>(`/api/buchungen/${id}`, e),
  buchungLoeschen: (id: number) => del(`/api/buchungen/${id}`),

  dauerauftraege: () => get<Dauerauftrag[]>('/api/dauerauftraege'),
  dauerauftragBuchungen: (id: number) => get<Buchung[]>(`/api/dauerauftraege/${id}/buchungen`),
  dauerauftragAnlegen: (e: DauerauftragEingabe) => post<Dauerauftrag>('/api/dauerauftraege', e),
  dauerauftragAendern: (id: number, e: DauerauftragEingabe) => put<Dauerauftrag>(`/api/dauerauftraege/${id}`, e),
  dauerauftragLoeschen: (id: number) => del(`/api/dauerauftraege/${id}`),
  dauerauftragAusfuehren: (id: number) => post<{ neueBuchungen: string[] }>(`/api/dauerauftraege/${id}/ausfuehren`),

  protokoll: () => get<ProtokollEintrag[]>('/api/protokoll'),
}
